import { forwardRef, ReactNode, useId } from "react";
import ReactSelect, { ClassNamesConfig, Props, components } from 'react-select';
import { twMerge } from "tailwind-merge";
import { badgeControlStyles, controlStyles, disabledControlStyles, styles } from "./styles";
import { replacePeriodToUnderscore } from "@/common/utils/transformers";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { PlacesType, Tooltip } from "react-tooltip";

export interface SelectProps extends Props {
  label?: string,
  markAsRequired?: boolean;
  labelIcon?: JSX.Element;
  disabled?: boolean,
  options?: { label: string | JSX.Element, value: number | string | null, isDisabled?: boolean, icon?: ReactNode }[];
  badge?: boolean;
  dataTestid?: string,
  customStyles?: ClassNamesConfig<unknown>;
  operationKlassToCheckPermission?: string;
  tooltipPlace?: PlacesType;
  tooltipMessage?: string;
  noPermissionText?: string;
  optionLabelFormat?: 'default' | 'icon',
}

const Select = forwardRef<HTMLElement, SelectProps>(({
  label,
  markAsRequired,
  labelIcon,
  className,
  disabled = false,
  options = [],
  placeholder = 'Selecione',
  badge = false,
  name,
  noOptionsMessage,
  dataTestid,
  customStyles,
  operationKlassToCheckPermission,
  tooltipPlace = "left",
  tooltipMessage,
  noPermissionText = 'Você não tem permissão',
  optionLabelFormat = 'default',
  ...props
}) => {
  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();
  const tooltipId = useId();

  const isPermissionNeeded = !!operationKlassToCheckPermission;
  const hasPermission = isPermissionNeeded && hasUserPermission(operationKlassToCheckPermission);

  const MenuList = (props: any) => {
    return (
      <div
        onWheel={(e) => e.stopPropagation()}
      >
        <components.MenuList {...props}>
          {props.children}
        </components.MenuList>
      </div>
    );
  };

  const formatOptionLabelWithIcon = (data: any) => (
    <div className='flex items-center gap-2'>
      {data.icon}
      <div>{data.label}</div>
    </div>
  );

  return (
    <div className={twMerge(className)}>
      <label
        className={twMerge("relative flex flex-col items-start justify-center",)}
        id={dataTestid || (name ? replacePeriodToUnderscore(name) + '_selector' : '')}
        data-testid={dataTestid || (name ? replacePeriodToUnderscore(name) + '_selector' : '')}
        data-tooltip-id={((isPermissionNeeded && !hasPermission) || tooltipMessage) ? tooltipId : ''}
      >
        {label && (
          <div className="flex items-center gap-x-2 mb-1" data-test-role="label">
            <p className={twMerge('text-xs', disabled ? 'text-neutral-4' : '')}>
              {label}
              {markAsRequired && (
                <span className="text-danger-soft"> *</span>
              )}
            </p>

            {labelIcon}
          </div>
        )}
        <ReactSelect
          components={{
            MenuList,
          }}
          options={options}
          styles={styles}
          classNames={customStyles || (badge ? badgeControlStyles : ((disabled || props.isDisabled) ? disabledControlStyles : controlStyles))}
          noOptionsMessage={!!noOptionsMessage ? noOptionsMessage : () => 'Nenhuma opção'}
          placeholder={placeholder}
          name={name}
          menuPlacement="auto"
          menuPosition="absolute"
          menuPortalTarget={document.body}
          {...props}
          formatOptionLabel={optionLabelFormat === "icon" ? formatOptionLabelWithIcon : undefined}
          isDisabled={props.isDisabled || (isPermissionNeeded && !hasPermission)}
          isLoading={props.isLoading || (isPermissionNeeded && isUserPermissionsPending)}
        />

          {((isPermissionNeeded && !hasPermission && !isUserPermissionsPending) || tooltipMessage) && (
          <Tooltip id={tooltipId} place={tooltipPlace} className="z-50">
            {tooltipMessage || noPermissionText}
          </Tooltip>
        )}
      </label>
    </div>
  )
});

export default Select;
